import { styled } from "@mui/material/styles";
import {
	Box,
	Button,
	Divider,
	Grid,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import React, { memo, useEffect } from "react";

import { useState } from "react";
import QuoteIcon from "../../assets/BlockQuoteImage.png";
import AppraisalsForm from "./AppraisalsForm";
import FAQs from "./FAQs";
import QuotesCarousel from "./QuotesCarousel";

const Heading = styled(Typography)`
	max-width: 400px;
	right: 16px;

	${(props) => props.theme.breakpoints.down("md")} {
		top: 10px;
		position: absolute;
		margin: 16px;
	}
`;

const Forklift = styled("img")`
	width: 78%;
	height: 200px;
	display: block;
	margin: 0px auto;
	object-fit: cover;
	object-position: 100% -10px;
`;

const GridBox = styled(Grid)`
	display: grid;
	grid-template-areas:
		"heading1 heading2 heading3 heading4"
		"content1 content2 content3 content4";
	column-gap: 32px;
	${(props) => props.theme.breakpoints.down("md")} {
		grid-template-areas:
			"heading1"
			"content1"
			"heading2"
			"content2"
			"heading3"
			"content3"
			"heading4"
			"content4";
		p {
			margin-bottom: 24px;
		}
	}
`;

const BannerFlex = styled(Box)`
	${(props) => props.theme.breakpoints.up("md")} {
		position: absolute;
		top: 20%;
		right: 16px;
		max-width: 400px;
	}
`;

const CtaButton = styled(Button, {
	// isVisible is not a valid prop on DOM elements. This prevents it from being passed to the DOM after styling
	shouldForwardProp: (prop) => prop !== 'isVisible',
})`
	${(props) => props.theme.breakpoints.down("md")} {
		position: fixed;
		bottom: 84px;
		left: 50%;
		transform: translate(-50%, -50%);
		z-index: 20;
		min-width: 192px;
		display: ${(props) => (props.isVisible ? "fixed" : "none")};
	}
`;

function Appraisals() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [content, setContent] = useState({});
	const [introImage, setIntroImage] = useState("");
	const [bannerImage, setBannerImage] = useState("");
	const [service1Icon, setService1Icon] = useState("");
	const [service2Icon, setService2Icon] = useState("");
	const [service3Icon, setService3Icon] = useState("");
	const [service4Icon, setService4Icon] = useState("");
	const [isVisible, setIsVisible] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const CmsResponse = await axios.get(
					process.env.REACT_APP_DIRECTUS_URL +
						"/items/Appraisals_Single?filter[status][_eq]=published"
				);
				const CmsData = CmsResponse.data.data[0];

				const assetBaseUrl =
					process.env.REACT_APP_DIRECTUS_URL + "/assets/";
				setIntroImage(assetBaseUrl + CmsData.intro_image);
				setBannerImage(assetBaseUrl + CmsData.banner_image);
				setService1Icon(assetBaseUrl + CmsData.service1_icon);
				setService2Icon(assetBaseUrl + CmsData.service2_icon);
				setService3Icon(assetBaseUrl + CmsData.service3_icon);
				setService4Icon(assetBaseUrl + CmsData.service4_icon);
				setContent(CmsData);
			} catch (err) {
				window.location.assign("/404");
			}
		})();
	}, []);

	useEffect(() => {
		window.posthog?.onFeatureFlags(() => {
			// PostHog and feature flags should be available at this point.
			if (window.posthog.isFeatureEnabled("record_appraisals_page")) {
				window.posthog.startSessionRecording();
			}
		});
	}, []);

	

	useEffect(() => {
		const listenToScroll = () => {
			let heightToHideFrom = 1000;
			const winScroll =
				document.body.scrollTop || document.documentElement.scrollTop;
	
			if (winScroll > heightToHideFrom) {
				isVisible && // to limit setting state only the first time
					setIsVisible(false);
			} else {
				setIsVisible(true);
			}
		};

		window.addEventListener("scroll", listenToScroll);
		return () => window.removeEventListener("scroll", listenToScroll);
	}, [isVisible]);


	return (
		<React.Fragment>
			<div style={{ position: "relative", top: "-3px" }}>
				<img
					alt="Worker talking to Appraiser"
					src={bannerImage}
					width="1160"
					height="387"
					style={{ width: "100%", height: "auto" }}
					data-testid="bannerImage"
				/>
				<BannerFlex>
					<Heading
						sx={{ color: "common.white" }}
						variant="h2"
						component="h1"
					>
						{content.banner_title}
					</Heading>
					{!isMobile && (
						<Typography
							sx={{ color: "common.white" }}
							variant="h3"
							component="h2"
						>
							{content.banner_subtitle}
						</Typography>
					)}
					<CtaButton
						href="#appraisals-form"
						id="appraisals-cta-button"
						isVisible={isVisible}
					>
						Request a quote now!
					</CtaButton>
				</BannerFlex>
			</div>

			<Box sx={{ p: "16px 28px 16px" }}>
				<Grid
					container
					alignItems="flex-start"
					sx={{
						mt: 4,
						mb: 8,
						textAlign: { xs: "center", md: "left" },
					}}
				>
					<Grid item xs={12} md={6}>
						<Typography variant="h2" sx={{ mb: 1 }}>
							{content.intro_title}
						</Typography>

						<Typography variant="body1" sx={{ mb: 2 }}>
							{content.intro_text}
						</Typography>

						<Typography
							variant="h2"
							sx={{
								color: "secondary.main",
								mb: "24px",
								"&.MuiTypography-root": { fontSize: "1rem" },
							}}
						>
							{content.intro_segments}
						</Typography>
					</Grid>
					<Box component={Grid} item xs={12} md={6}>
						{introImage && (
							<Forklift
								src={introImage}
								alt="Construction Worker Reading Tablet"
								data-testid="introImage"
							/>
						)}
					</Box>
				</Grid>
				<Divider />
				<img
					alt="Quotes"
					src={QuoteIcon}
					width="55"
					style={{
						display: "block",
						position: "relative",
						top: "-32px",
						margin: "0 auto",
					}}
				/>

				<QuotesCarousel />
				<Divider sx={{ mb: 4, mt: 4 }} />
				<Grid container justifyContent="flex-start">
					<Grid item xs={12}>
						<Typography variant="h2" sx={{ textAlign: "center" }}>
							{content.services_title}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Typography
							variant="h3"
							sx={{ textAlign: "center", mb: 3 }}
						>
							{content.services_subtitle}
						</Typography>
					</Grid>
				</Grid>
				<GridBox
					columnSpacing={4}
					sx={{ textAlign: { xs: "center", md: "left" } }}
				>
					<Box sx={{ gridArea: "heading1" }}>
						<img
							alt="Tractors on Desktop Screen"
							src={service1Icon}
							width="60px"
							sx={{ color: "secondary.icon" }}
							data-testid="service1Icon"
						/>
						<Typography
							variant="h3"
							sx={{ mt: 2, color: "secondary.icon" }}
						>
							<strong>{content.service1_title}</strong>
						</Typography>
					</Box>
					<Box sx={{ gridArea: "content1" }}>
						<Typography sx={{ mb: 2 }}>
							{content.service1_text}
						</Typography>
					</Box>

					<Box sx={{ gridArea: "heading2" }}>
						<img
							alt="Tractors on Desktop Screen"
							src={service2Icon}
							width="60px"
							sx={{ color: "secondary.icon" }}
							data-testid="service2Icon"
						/>
						<Typography
							variant="h3"
							sx={{ mb: 2, mt: 2, color: "secondary.icon" }}
						>
							<strong>{content.service2_title}</strong>
						</Typography>
					</Box>
					<Box sx={{ gridArea: "content2" }}>
						<Typography sx={{ mb: 2 }}>
							{content.service2_text}
						</Typography>
					</Box>
					<Box sx={{ gridArea: "heading3" }}>
						<img
							alt="Hard Hat"
							src={service3Icon}
							width="60px"
							sx={{ color: "secondary.icon" }}
							data-testid="service3Icon"
						/>
						<Typography
							variant="h3"
							sx={{ mb: 2, mt: 2, color: "secondary.icon" }}
						>
							<strong>{content.service3_title}</strong>
						</Typography>
					</Box>
					<Box sx={{ gridArea: "content3" }}>
						<Typography sx={{ mb: 2 }}>
							{content.service3_text}
						</Typography>
					</Box>
					<Box sx={{ gridArea: "heading4" }}>
						<img
							alt="Weighing Scale"
							src={service4Icon}
							width="60px"
							sx={{ color: "secondary.icon" }}
							data-testid="service4Icon"
						/>
						<Typography
							variant="h3"
							sx={{ mb: 2, mt: 2, color: "secondary.icon" }}
						>
							<strong>{content.service4_title}</strong>
						</Typography>
					</Box>
					<Box sx={{ gridArea: "content4" }}>
						<Typography sx={{ mb: 2 }}>
							{content.service4_text}
						</Typography>
					</Box>
				</GridBox>
				<Grid container sx={{ mb: 4 }}>
					<Grid item xs={12}>
						<Typography
							variant="h2"
							sx={{ textAlign: "center", mb: 3 }}
						>
							{content.FAQ_title}
						</Typography>
					</Grid>
					<FAQs />
				</Grid>
				<Grid container columnSpacing={3}>
					<Grid item xs={12} md={6}>
						<Typography variant="h2">
							<em>{content.form_title}</em>
						</Typography>
						{!isMobile && (
							<Typography>{content.form_text}</Typography>
						)}
					</Grid>
					<Grid item xs={12} md={6}>
						<AppraisalsForm />
					</Grid>
				</Grid>
			</Box>
		</React.Fragment>
	);
}

export default memo(Appraisals);
